import { apiClient } from "./api.js";

/**
 * Super Admin Analytics Service
 * Handles all analytics API calls for super admin dashboard
 */
class SuperAdminAnalyticsService {
  /**
   * Get platform overview analytics
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} Platform overview data
   */
  async getPlatformOverview(params = {}) {
    try {
      const response = await apiClient.get(
        "/super-admin/analytics/platform-overview",
        {
          params: {
            timeRange: params.timeRange || "6months",
            startDate: params.startDate,
            endDate: params.endDate,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching platform overview:", error);
      throw this.handleError(error);
    }
  }

  /**
   * Get user growth analytics
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} User growth data
   */
  async getUserGrowth(params = {}) {
    try {
      const response = await apiClient.get(
        "/super-admin/analytics/user-growth",
        {
          params: {
            timeRange: params.timeRange || "6months",
            startDate: params.startDate,
            endDate: params.endDate,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching user growth:", error);
      throw this.handleError(error);
    }
  }

  /**
   * Get revenue analytics
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} Revenue analytics data
   */
  async getRevenueAnalytics(params = {}) {
    try {
      const response = await apiClient.get("/super-admin/analytics/revenue", {
        params: {
          timeRange: params.timeRange || "6months",
          startDate: params.startDate,
          endDate: params.endDate,
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching revenue analytics:", error);
      throw this.handleError(error);
    }
  }

  /**
   * Get performance analytics
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} Performance analytics data
   */
  async getPerformanceAnalytics(params = {}) {
    try {
      const response = await apiClient.get(
        "/super-admin/analytics/performance",
        {
          params: {
            timeRange: params.timeRange || "6months",
            startDate: params.startDate,
            endDate: params.endDate,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching performance analytics:", error);
      throw this.handleError(error);
    }
  }

  /**
   * Get geographic analytics
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} Geographic analytics data
   */
  async getGeographicAnalytics(params = {}) {
    try {
      const response = await apiClient.get(
        "/super-admin/analytics/geographic",
        {
          params: {
            timeRange: params.timeRange || "6months",
            startDate: params.startDate,
            endDate: params.endDate,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching geographic analytics:", error);
      throw this.handleError(error);
    }
  }

  /**
   * Get comprehensive dashboard analytics
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} Complete dashboard data
   */
  async getDashboardAnalytics(params = {}) {
    try {
      const response = await apiClient.get("/super-admin/analytics/dashboard", {
        params: {
          timeRange: params.timeRange || "6months",
          startDate: params.startDate,
          endDate: params.endDate,
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching dashboard analytics:", error);
      throw this.handleError(error);
    }
  }

  /**
   * Get analytics data for specific metric
   * @param {string} metric - Metric type (overview, users, revenue, performance, geographic)
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} Analytics data
   */
  async getAnalyticsByMetric(metric, params = {}) {
    const methods = {
      overview: this.getPlatformOverview,
      users: this.getUserGrowth,
      revenue: this.getRevenueAnalytics,
      performance: this.getPerformanceAnalytics,
      geographic: this.getGeographicAnalytics,
    };

    if (!methods[metric]) {
      throw new Error(`Invalid metric: ${metric}`);
    }

    return await methods[metric].call(this, params);
  }

  /**
   * Handle API errors
   * @param {Error} error - Error object
   * @returns {Error} Formatted error
   */
  handleError(error) {
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;
      return new Error(data?.message || `Server error: ${status}`);
    } else if (error.request) {
      // Request was made but no response received
      return new Error("Network error: Unable to connect to server");
    } else {
      // Something else happened
      return new Error(error.message || "An unexpected error occurred");
    }
  }

  /**
   * Get fallback data for charts when API fails
   * @param {string} metric - Metric type
   * @returns {Object} Fallback data
   */
  getFallbackData(metric) {
    const fallbackData = {
      overview: {
        summary: {
          totalUsers: 0,
          activeUsers: 0,
          totalRevenue: 0,
          totalTrips: 0,
          avgResponseTime: 0,
          systemUptime: 0,
        },
        chartData: [],
      },
      users: {
        chartData: [],
        summary: {
          totalNewUsers: 0,
          avgDailyActiveUsers: 0,
          avgChurnRate: 0,
        },
      },
      revenue: {
        breakdownData: [],
        hospitalPerformance: [],
        summary: {
          totalRevenue: 0,
          avgRevenuePerTrip: 0,
          topPerformingHospital: "N/A",
        },
      },
      performance: {
        driverPerformance: [],
        systemMetrics: [],
        summary: {
          totalDrivers: 0,
          avgResponseTime: 0,
          totalEarnings: 0,
        },
      },
      geographic: {
        chartData: [],
        summary: {
          totalCities: 0,
          topPerformingCity: "N/A",
          totalTrips: 0,
          totalRevenue: 0,
        },
      },
    };

    return fallbackData[metric] || fallbackData.overview;
  }

  /**
   * Format date range for API calls
   * @param {string} timeRange - Time range (1month, 3months, 6months, 1year)
   * @returns {Object} Date range object
   */
  formatDateRange(timeRange) {
    const now = new Date();
    let startDate;

    switch (timeRange) {
      case "1month":
        startDate = new Date(now.getFullYear(), now.getMonth(), 1); // Current month only
        break;
      case "3months": {
        const targetMonth = now.getMonth() - 2;
        const targetYear =
          targetMonth < 0 ? now.getFullYear() - 1 : now.getFullYear();
        const adjustedMonth = targetMonth < 0 ? targetMonth + 12 : targetMonth;
        startDate = new Date(targetYear, adjustedMonth, 1);
        break;
      }
      case "6months": {
        const targetMonth = now.getMonth() - 5;
        const targetYear =
          targetMonth < 0 ? now.getFullYear() - 1 : now.getFullYear();
        const adjustedMonth = targetMonth < 0 ? targetMonth + 12 : targetMonth;
        startDate = new Date(targetYear, adjustedMonth, 1);
        break;
      }
      case "1year": {
        const targetMonth = now.getMonth() - 11;
        const targetYear =
          targetMonth < 0 ? now.getFullYear() - 1 : now.getFullYear();
        const adjustedMonth = targetMonth < 0 ? targetMonth + 12 : targetMonth;
        startDate = new Date(targetYear, adjustedMonth, 1);
        break;
      }
      default:
        startDate = new Date(now.getFullYear(), now.getMonth() - 5, 1); // Default to 6 months
    }

    return {
      startDate:
        startDate.getFullYear() +
        "-" +
        String(startDate.getMonth() + 1).padStart(2, "0") +
        "-" +
        String(startDate.getDate()).padStart(2, "0"),
      endDate:
        now.getFullYear() +
        "-" +
        String(now.getMonth() + 1).padStart(2, "0") +
        "-" +
        String(now.getDate()).padStart(2, "0"),
    };
  }
}

// Create and export singleton instance
const superAdminAnalyticsService = new SuperAdminAnalyticsService();
export default superAdminAnalyticsService;
